import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Code2, Network, BarChart3, Users, Server, 
  ArrowRight, CheckCircle, Shield, Award, Clock, ChevronRight,
  Landmark, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f4acee42-6143-402b-b6a2-a84038bb5df0/artifacts/wp0qjcw1_WhatsApp%20Image%202026-03-11%20at%2011.51.52%20AM.jpeg";

const iconMap = {
  Building2,
  Code2,
  Network,
  BarChart3,
  Users,
  Server,
};

const stats = [
  { value: '150+', label: 'Projects Delivered', icon: CheckCircle },
  { value: '50+', label: 'Government Clients', icon: Landmark },
  { value: '15+', label: 'Years Experience', icon: Clock },
  { value: '100%', label: 'Client Satisfaction', icon: Award },
];

const partners = [
  'Haryana Govt',
  'Nagar Parishad',
  'Smart City',
  'NDCC Portal',
  'Revenue Dept',
  'Transport Corp',
];

export default function Home() {
  const [services, setServices] = useState([]);
  const [govProjects, setGovProjects] = useState([]);
  const [privateProjects, setPrivateProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, govRes, privateRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/projects?category=government`),
          axios.get(`${API}/projects?category=private`),
        ]);
        setServices(servicesRes.data.slice(0, 6));
        setGovProjects(govRes.data.slice(0, 4));
        setPrivateProjects(privateRes.data.slice(0, 2));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0f1419]">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1714722804938-18717a9dc6dc?w=1920&q=80)'
          }}
          aria-label="Indian city skyline at night"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1419]/95 via-[#0f1419]/80 to-[#0f1419]/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 w-full">
          <div className="max-w-3xl">
            <Badge className="bg-nstu-gold-400 text-[#0f1419] mb-6 px-4 py-2 text-sm font-bold animate-fadeInUp">
              Trusted Haryana Government IT Partner
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fadeInUp stagger-1">
              Building Haryana's Digital Infrastructure
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed animate-fadeInUp stagger-2">
              NSTU India Pvt. Ltd. delivers cutting-edge IT solutions for Government departments 
              and enterprises. From e-Governance platforms to property tax systems, we transform 
              visions into digital reality across Haryana.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp stagger-3">
              <Link to="/government-projects">
                <Button size="lg" className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 font-bold px-8 py-6 text-lg btn-lift" data-testid="hero-gov-projects-btn">
                  <Landmark className="w-5 h-5 mr-2" />
                  Government Projects
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-nstu-gold-400 text-nstu-gold-400 hover:bg-nstu-gold-400 hover:text-[#0f1419] font-semibold px-8 py-6 text-lg" data-testid="hero-contact-btn">
                  Get In Touch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges / Partners */}
      <section className="py-12 bg-[#141a22] border-y border-gray-800" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-sm text-nstu-gold-400 uppercase tracking-widest mb-8 font-medium">
            Trusted by Haryana Government & Organizations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
            {partners.map((partner) => (
              <div 
                key={partner}
                className="px-6 py-3 bg-[#1a222d] rounded-lg border border-gray-700 text-gray-300 font-medium hover:border-nstu-gold-400 hover:text-nstu-gold-400 transition-all"
              >
                {partner}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0f1419]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => {
              const IconComponent = stat.icon;
              return (
                <div 
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-[#1a222d] border border-gray-800 card-hover"
                >
                  <IconComponent className="w-10 h-10 text-nstu-gold-400 mx-auto mb-4" />
                  <div className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <p className="text-gray-400 font-medium">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-[#141a22]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-nstu-gold-400/20 text-nstu-gold-400 border border-nstu-gold-400/30 mb-4">Our Expertise</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Comprehensive IT Solutions
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              End-to-end technology services designed for government departments and enterprise clients
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Server;
              return (
                <Card 
                  key={service.id}
                  className="group bg-[#1a222d] border border-gray-800 hover:border-nstu-gold-400 transition-all duration-300 card-hover overflow-hidden"
                  data-testid={`service-card-${service.id}`}
                >
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-nstu-green-800 flex items-center justify-center mb-6 group-hover:bg-nstu-gold-400 transition-colors">
                      <IconComponent className="w-7 h-7 text-white group-hover:text-[#0f1419]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-4 line-clamp-2">
                      {service.description}
                    </p>
                    <Link 
                      to="/services"
                      className="inline-flex items-center text-nstu-gold-400 hover:text-nstu-gold-300 font-semibold transition-colors"
                    >
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-nstu-gold-400 text-nstu-gold-400 hover:bg-nstu-gold-400 hover:text-[#0f1419] px-8" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Government Projects Section - Highlighted */}
      <section className="py-20 bg-nstu-green-800 relative overflow-hidden" data-testid="gov-projects-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Landmark className="w-8 h-8 text-nstu-gold-400" />
                <Badge className="bg-nstu-gold-400 text-nstu-green-900 font-semibold">Featured</Badge>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                Haryana Government Projects
              </h2>
              <p className="text-gray-300 max-w-xl">
                Driving digital transformation across Haryana - Nagar Parishads, Revenue Departments, and Municipal Corporations
              </p>
            </div>
            <Link to="/government-projects">
              <Button className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-semibold btn-lift" data-testid="view-gov-projects-btn">
                View All Government Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {govProjects.map((project) => (
              <div 
                key={project.id}
                className="group relative bg-[#1a222d] rounded-xl overflow-hidden shadow-xl card-hover border border-gray-700"
                data-testid={`gov-project-${project.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-nstu-gold-400 text-[#0f1419]">Haryana Govt</Badge>
                    <span className="text-sm text-gray-500">{project.year}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{project.description}</p>
                  <p className="text-sm text-nstu-gold-400 font-medium">{project.client}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Projects Section */}
      <section className="py-20 bg-[#0f1419]" data-testid="private-projects-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Briefcase className="w-8 h-8 text-nstu-gold-400" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                Enterprise Solutions
              </h2>
              <p className="text-gray-400 max-w-xl">
                Empowering private sector clients with scalable and innovative technology solutions
              </p>
            </div>
            <Link to="/private-projects">
              <Button variant="outline" className="border-2 border-nstu-gold-400 text-nstu-gold-400 hover:bg-nstu-gold-400 hover:text-[#0f1419] font-semibold" data-testid="view-private-projects-btn">
                View Private Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {privateProjects.map((project) => (
              <div 
                key={project.id}
                className="group bg-[#1a222d] rounded-xl overflow-hidden border border-gray-800 hover:border-nstu-gold-400 transition-all card-hover"
                data-testid={`private-project-${project.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-gray-700 text-gray-300">Private Sector</Badge>
                    <span className="text-sm text-gray-500">{project.year}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-3 line-clamp-2">{project.description}</p>
                  <p className="text-sm text-nstu-gold-400 font-medium">{project.client}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#141a22]" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-nstu-gold-400/20 text-nstu-gold-400 border border-nstu-gold-400/30 mb-4">Why NSTU?</Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Your Trusted Partner for Haryana Government IT Projects
              </h2>
              <p className="text-gray-400 mb-8">
                With extensive experience in Haryana government IT projects, we understand the unique 
                requirements of property tax systems, bill distribution, and citizen services. 
                Our solutions are built for security, scalability, and compliance.
              </p>
              <ul className="space-y-4">
                {[
                  'Haryana Government empanelled IT company',
                  'Property Tax Bill Distribution expertise',
                  'NDCC Portal integration specialists',
                  'Proven track record in Nagar Parishad projects',
                  'Pan-Haryana presence with local support'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-nstu-gold-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/about">
                  <Button className="bg-nstu-gold-400 text-[#0f1419] hover:bg-nstu-gold-300 btn-lift font-semibold" data-testid="learn-more-btn">
                    Learn More About Us
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
                <img 
                  src="https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="NSTU Team Working"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-8 -left-8 bg-[#1a222d] p-6 rounded-xl shadow-xl border-l-4 border-nstu-gold-400 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-nstu-green-800 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-white">Security First</p>
                    <p className="text-sm text-gray-400">Enterprise-grade security</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-nstu-green-800" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Organization?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Partner with NSTU for reliable, secure, and innovative IT solutions. 
            Let's discuss your project requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-nstu-gold-400 text-nstu-green-900 hover:bg-nstu-gold-300 font-bold px-8 py-6 text-lg btn-lift" data-testid="cta-contact-btn">
                Start a Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+919541377777">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nstu-green-800 font-semibold px-8 py-6 text-lg">
                Call +91 95413 77777
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
